/**
 * Session-upload list state for the Fleet Control Files tab.
 *
 * Three rules this module exists to enforce:
 *
 *   1. **No refresh for a file this client just uploaded.** The upload
 *      response carries the full record, so we merge it in directly. In the
 *      recording the operator attached a PNG and the Files tab stayed empty
 *      because the tab only knew about project diffs — but even for a
 *      diff-aware tab, making the operator click Refresh to see their own
 *      upload reads as "it didn't work".
 *   2. **Every file for the session, not just this turn.** The list is keyed
 *      by session, so reloading shows uploads from earlier turns.
 *   3. **Refresh failures are local.** A failed list call sets `error` on
 *      this panel; it never raises a session-level banner.
 */

import { listSessionFiles, type SessionFileRecord } from "../chat/session-files.ts";

/**
 * Cap a session-keyed map by evicting the oldest-inserted entries.
 *
 * Used for the inspector's write-through caches (`sessionEnvironmentBySession`,
 * `chatContextBreakdownBySession`). Their comment claimed a cap that was never
 * implemented — only `chatPalaceContinuityCache` actually evicted — so every
 * session the operator visited left an entry behind for the life of the page.
 *
 * Re-inserts the key first so Map iteration order is recency-ordered, which
 * is what makes "oldest" well-defined.
 */
export function capSessionKeyedMap<K, V>(map: Map<K, V>, maxEntries: number): void {
  while (map.size > maxEntries) {
    const oldest = map.keys().next().value as K | undefined;
    if (oldest === undefined) break;
    map.delete(oldest);
  }
}

/**
 * Record `value` for `key` and re-insert so Map order is recency-ordered
 * before capping. Call this instead of a bare `map.set` on a session-keyed
 * cache so the eviction above has a well-defined victim.
 */
export function rememberSessionKeyedEntry<K, V>(
  map: Map<K, V>,
  key: K,
  value: V,
  maxEntries: number,
): void {
  map.delete(key);
  map.set(key, value);
  capSessionKeyedMap(map, maxEntries);
}

/** Sessions retained by the inspector's per-session write-through caches. */
export const SESSION_KEYED_CACHE_MAX_SESSIONS = 16;

export type SessionUploadsState = {
  sessionKey: string | null;
  files: SessionFileRecord[];
  loading: boolean;
  error: string | null;
  loaded: boolean;
};

/**
 * How many rows the IndexedDB `drafts` store keeps.
 *
 * The store is keyed by session key and had no cap and no eviction, so a
 * closed tab was the only thing that removed a row. Each row holds a
 * `ChatAttachment[]` — and before the session-file rework those attachments
 * carried base64 `dataUrl` strings, so a single draft could pin ~85 KB of
 * encoded media per file for the life of the browser profile.
 *
 * Mirrors `SCROLL_ANCHOR_FIFO_CAP` in the same module, which is the
 * existing precedent for capping an IDB store by insertion order.
 */
export const SESSION_DRAFTS_FIFO_CAP = 64;

export function emptySessionUploadsState(): SessionUploadsState {
  return { sessionKey: null, files: [], loading: false, error: null, loaded: false };
}

/** Merge one freshly-stored record into the list, newest last. */
export function mergeUploadedFile(
  state: SessionUploadsState,
  sessionKey: string,
  file: SessionFileRecord,
): SessionUploadsState {
  // A record for a different session means the operator switched sessions
  // mid-upload; drop it rather than showing a foreign row.
  if (state.sessionKey !== null && state.sessionKey !== sessionKey) {
    return state;
  }
  const existingIdx = state.files.findIndex((f) => f.fileId === file.fileId);
  const files = [...state.files];
  if (existingIdx >= 0) {
    // Replace so a later preparation summary (duration, derivatives) lands.
    files[existingIdx] = { ...files[existingIdx], ...file };
  } else {
    files.push(file);
  }
  files.sort((a, b) => a.createdAt - b.createdAt);
  return { sessionKey, files, loading: false, error: null, loaded: true };
}

/** Drop state when the operator switches sessions. */
export function resetForSession(
  state: SessionUploadsState,
  sessionKey: string | null,
): SessionUploadsState {
  if (state.sessionKey === sessionKey) return state;
  return { ...emptySessionUploadsState(), sessionKey };
}

/** Find the message that referenced a file, for the "In chat" jump. */
export function findTranscriptMessageIdForFile(
  messages: readonly unknown[] | undefined,
  fileId: string,
): string | null {
  for (const message of messages ?? []) {
    const refs = (message as { sessionFiles?: unknown })?.sessionFiles;
    if (!Array.isArray(refs)) continue;
    const hit = refs.some(
      (ref) =>
        typeof (ref as { fileId?: unknown })?.fileId === "string" &&
        (ref as { fileId: string }).fileId === fileId,
    );
    if (!hit) continue;
    const id = (message as { id?: unknown }).id;
    if (typeof id === "string" && id) return id;
    const transcriptId = (message as { transcriptId?: unknown }).transcriptId;
    if (typeof transcriptId === "string" && transcriptId) return transcriptId;
  }
  return null;
}

/**
 * Load every file stored for a session. Resolves to the list; callers assign
 * it onto {@link SessionUploadsState}. Never throws.
 */
export async function loadSessionUploads(args: {
  origin: string;
  token: string | undefined;
  sessionKey: string;
}): Promise<SessionFileRecord[]> {
  try {
    return await listSessionFiles(args);
  } catch {
    return [];
  }
}
